import React, {useCallback, useMemo, useState} from 'react';

import {Dialog, Props as DialogProps} from '#/Dialog/Dialog';

export type UseDialogControllerReturnType = {
  isVisible: boolean;
  showDialog: () => void;
  closeDialog: () => void;
};

export const useDialogController = (): UseDialogControllerReturnType => {
  const [isVisible, setIsVisible] = useState(false);
  const showDialog = useCallback(() => setIsVisible(true), []);
  const closeDialog = useCallback(() => setIsVisible(false), []);
  return {isVisible, showDialog, closeDialog};
};

/**
 * Creates a dynamic dialog. Its title, content, and buttons can be changed by
 * passing a state to the `showDialog` callback.
 */
export const useDynamicDialog = <T,>(
  {...dialogProps}: React.PropsWithChildren<DynamicDialogProps<T>>,
  content?: (arg: T) => React.ReactNode
): UseDynamicDialogReturnType<T> => {
  const [state, setState] = useState<T>();
  const [isVisible, setIsVisible] = useState(false);
  const showDialog = useCallback((userState: T) => {
    setState(userState);
    setIsVisible(true);
  }, []);
  const closeDialog = useCallback(() => setIsVisible(false), []);

  const parametrizedDialogProps = useMemo(() => {
    if (state === undefined) {
      return {};
    }
    return {
      actions: dialogProps.actions?.(state),
      headerText: dialogProps.headerText?.(state),
      descriptionText: dialogProps.descriptionText?.(state),
    };
  }, [dialogProps, state]);

  return {
    closeDialog,
    dialog: (
      <>
        {isVisible && (
          <Dialog
            isShown={isVisible}
            onClose={closeDialog}
            {...parametrizedDialogProps}
          >
            {content && state ? content(state) : null}
          </Dialog>
        )}
      </>
    ),
    isVisible,
    showDialog,
  };
};

type DynamicDialogProps<T> = {
  actions?: (state: T) => DialogProps['actions'];
  headerText?: (state: T) => DialogProps['headerText'];
  descriptionText?: (state: T) => DialogProps['descriptionText'];
};

type UseDynamicDialogReturnType<T = void> = {
  closeDialog: () => void;
  dialog: React.ReactElement;
  isVisible: boolean;
  showDialog: (arg: T) => void;
};
